import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import MobileSolicitacoes from "./MobileSolicitacoes";
import MobilePerfil from "./MobilePerfil";
import MobileReservas from "./MobileReservas";
import MobileFamiliares from "./MobileFamiliares";
import MobilePrestadores from "./MobilePrestadores";
import MobileVisitantes from "./MobileVisitantes";
import MobileQrAcesso from "./MobileQrAcesso";
import MobileFacial from "./MobileFacial";
import MobileVoz from "./MobileVoz";
import MobileConvites from "./MobileConvites";
import MobilePreAgenda from "./MobilePreAgenda";
import { MobilePrivacyConsent } from "@/components/MobilePrivacyConsent";
import { MobileResponsabilidades, RESP_CHAVE } from "@/components/MobileResponsabilidades";
import {
  Menu, X, Box, QrCode, User, Home, Mail, FileSpreadsheet,
  Users, Contact, HardHat, Shield, FileText, CalendarDays,
  Megaphone, Video, AudioLines, LogOut, ChevronDown, ArrowRight, Siren, Ambulance, Flame, Loader2, Check, UserX, Info,
} from "lucide-react";

const FONT_MAIN = "'Hanken Grotesk', 'Inter', sans-serif";
const BLUE = "#2563EB";
const REVENDA = "Interaccess Sistemas. Versão 1.1.3";

const fmtAvisoData = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" }) + " " +
      d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
};

function AvisoItem({ a, onClick }) {
  return (
    <button type="button" data-testid={`appmobile-aviso-item-${a.id}`} onClick={onClick}
      className="w-full text-left rounded-xl px-3 py-2.5 border border-white/5 bg-[rgba(10,11,16,0.55)] hover:bg-[rgba(58,62,80,0.75)] active:scale-[0.98] transition-all">
      <div className="flex items-center gap-2 min-w-0">
        <span className="flex-1 min-w-0 truncate text-xs font-semibold" style={{ color: "#e1e2ed" }}>{a.title}</span>
        {a.lido
          ? <Check className="h-4 w-4 shrink-0" style={{ color: "#7ef0a0" }} data-testid={`appmobile-aviso-lido-${a.id}`} />
          : <span className="shrink-0 w-4 h-4 rounded-full text-[10px] font-bold flex items-center justify-center"
              style={{ background: "rgba(244,162,97,0.2)", color: "#f4a261" }} data-testid={`appmobile-aviso-naolido-${a.id}`}>?</span>}
      </div>
      <div className="flex items-center justify-between mt-1">
        <span className="text-[10px]" style={{ color: "#8d90a0" }}>{fmtAvisoData(a.created_at)}</span>
        <span className="text-[10px] font-medium" style={{ color: a.lido ? "#7ef0a0" : "#f4a261" }}>
          {a.lido ? "Lido" : "Não lido"}
        </span>
      </div>
    </button>
  );
}

const EMERG_CSS = `@keyframes emergBlink { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(0.8); opacity: 0.45; } }
.emerg-blink { animation: emergBlink 0.28s ease-in-out 3; }`;

const glassCard = {
  background: "rgba(40, 42, 50, 0.6)",
  backdropFilter: "blur(16px)",
  WebkitBackdropFilter: "blur(16px)",
  border: "1px solid rgba(141, 144, 160, 0.5)",
  borderTop: "1px solid rgba(255, 255, 255, 0.1)",
};

const _parseAgendaData = (s) => {
  if (!s) return null;
  const d = new Date(String(s).trim().replace(" ", "T"));
  return isNaN(d) ? null : d;
};
const agendaVigenteOuFutura = (v) => {
  const ini = _parseAgendaData(v.permanencia_inicial);
  const fim = _parseAgendaData(v.permanencia_final);
  if (!ini && !fim) return false;
  if (fim && new Date() > fim) return false;
  return true;
};

function CountBadge({ n, testid, floating = false }) {
  return (
    <span data-testid={testid}
      className={`min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold inline-flex items-center justify-center ${floating ? "absolute top-1 right-1" : ""}`}
      style={{ background: "rgba(141, 144, 160, 0.4)", color: "#e1e2ed" }}>
      {n}
    </span>
  );
}

function GlowLine() {
  return (
    <span className="absolute top-0 left-[6%] right-[6%] h-px pointer-events-none z-10"
      style={{ background: "linear-gradient(90deg, transparent, rgba(178, 205, 245, 0.85), transparent)", boxShadow: "0 0 10px rgba(120, 165, 230, 0.45)", borderRadius: 999 }} />
  );
}

function IconBadge({ icon: Icon, color, size = "h-4 w-4", box = "w-8 h-8" }) {
  return (
    <span className={`${box} rounded-lg flex items-center justify-center shrink-0`}
      style={{ background: `${color}22`, border: `1px solid ${color}33` }}>
      <Icon className={size} style={{ color }} />
    </span>
  );
}

function SquareButton({ icon: Icon, label, color, badge, testid, onClick }) {
  return (
    <button type="button" data-testid={testid}
      onClick={onClick || (() => toast.info(`${label}: em breve.`))}
      className="relative flex flex-col items-center justify-center gap-2 rounded-xl py-4 px-1 border border-white/5 bg-[rgba(10,11,16,0.55)] hover:bg-[rgba(58,62,80,0.75)] active:scale-[0.97] transition-all duration-200">
      {badge != null && <CountBadge n={badge} testid={`${testid}-badge`} floating />}
      <Icon className="h-6 w-6" style={{ color }} />
      <span className="text-xs font-medium" style={{ color: "#c3c6d7" }}>{label}</span>
    </button>
  );
}

function Collapsible({ icon: Icon, iconColor = "#f4a261", title, badge, right, open, onToggle, testid, sectionRef, children }) {
  const chevron = <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${open ? "rotate-180" : ""}`} style={{ color: "#8d90a0" }} />;
  return (
    <section ref={sectionRef} data-testid={testid} data-open={open ? "true" : "false"} className="relative rounded-[14px] overflow-hidden" style={glassCard}>
      <GlowLine />
      <div className="w-full flex items-center gap-2.5 px-4 py-3.5">
        <button type="button" data-testid={`${testid}-toggle`} onClick={onToggle}
          className="flex-1 flex items-center gap-2.5 text-left min-w-0">
          {Icon && <IconBadge icon={Icon} color={iconColor} />}
          <span className="text-sm font-semibold tracking-wide" style={{ color: "#e1e2ed" }}>{title}</span>
          {badge != null && <CountBadge n={badge} testid={`${testid}-badge`} />}
          {!right && <span className="ml-auto">{chevron}</span>}
        </button>
        {right}
        {right && (
          <button type="button" data-testid={`${testid}-toggle-icon`} onClick={onToggle} aria-label="Alternar seção"
            className="w-8 h-8 flex items-center justify-center rounded-lg active:bg-white/10">
            {chevron}
          </button>
        )}
      </div>
      <div className="grid transition-[grid-template-rows] duration-300" style={{ gridTemplateRows: open ? "1fr" : "0fr" }}>
        <div className="overflow-hidden">
          <div className="px-4 pb-4">{children}</div>
        </div>
      </div>
    </section>
  );
}

function TabBarButton({ icon: Icon, label, testid, onClick, color = "#c3c6d7", blink = false }) {
  return (
    <button type="button" data-testid={testid} onClick={onClick}
      className={`flex-1 flex flex-col items-center justify-center gap-1.5 py-3 rounded-xl active:bg-white/5 transition-colors ${blink ? "emerg-blink" : ""}`}>
      <IconBadge icon={Icon} color={color} />
      <span className="text-[11px] font-medium" style={{ color }}>{label}</span>
    </button>
  );
}

export default function MobileDashboard({ tenant, condo, unidade, nome, origem, cpf, tipo, onLogoff }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [drawerDrag, setDrawerDrag] = useState(0);
  const drawerTouchRef = useRef(null);
  const drawerDragRef = useRef(0);
  const [respAberto, setRespAberto] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem(RESP_CHAVE)) setRespAberto(true);
  }, []);
  const [openUnidade, setOpenUnidade] = useState(false);
  const [openAdm, setOpenAdm] = useState(false);
  const [openAvisos, setOpenAvisos] = useState(false);
  const [emergOpen, setEmergOpen] = useState(false);
  const [enviando, setEnviando] = useState(null);
  const [blinkEmerg, setBlinkEmerg] = useState(false);
  const [avisos, setAvisos] = useState([]);
  const [naoLidos, setNaoLidos] = useState(0);
  const [avisoView, setAvisoView] = useState(null);
  const [solicOpen, setSolicOpen] = useState(false);
  const [todosOpen, setTodosOpen] = useState(false);
  const [solicAndamento, setSolicAndamento] = useState(0);
  const [perfilOpen, setPerfilOpen] = useState(false);
  const [reservasOpen, setReservasOpen] = useState(false);
  const [familiaresOpen, setFamiliaresOpen] = useState(false);
  const [prestadoresOpen, setPrestadoresOpen] = useState(false);
  const [visitantesOpen, setVisitantesOpen] = useState(false);
  const [qrAcessoOpen, setQrAcessoOpen] = useState(false);
  const [facialModo, setFacialModo] = useState(null); // null | "enroll" | "verify"
  const [facialToken, setFacialToken] = useState(null);
  const [bloqueioOpen, setBloqueioOpen] = useState(false);
  const [bloqueioS, setBloqueioS] = useState(60);
  const [vozOpen, setVozOpen] = useState(false);
  const [vozAtivo, setVozAtivo] = useState(false);
  const [vozComandos, setVozComandos] = useState([]);
  const [facialDestino, setFacialDestino] = useState("qr"); // "qr" | "voz"

  const carregarVoz = useCallback(async () => {
    try {
      const r = await fetch(`${API}/public/voz/comandos?origem=${encodeURIComponent(origem)}&eql=${encodeURIComponent(unidade)}&cpf=${cpf}`);
      const d = await r.json();
      if (d.ok) {
        setVozAtivo(!!d.ativo);
        setVozComandos(d.comandos || []);
      }
    } catch { /* silencioso */ }
  }, [origem, unidade, cpf]);

  // Gate (exigência Apple): QR Acesso, Convites, Pré Agenda e Voice só funcionam após aceite dos termos
  const exigirTermos = (acao) => {
    if (localStorage.getItem(RESP_CHAVE) !== "1") { setRespAberto(true); return; }
    acao();
  };

  const abrirQrAcesso = async () => {
    setFacialDestino("qr");
    try {
      const r = await fetch(`${API}/public/facial/status?origem=${encodeURIComponent(origem)}&eql=${encodeURIComponent(unidade)}&cpf=${cpf}`);
      const d = await r.json();
      if (d.ok && d.tenant_2fa && !d.liberar_2fa) {
        if ((d.bloqueado_s || 0) > 0) {
          setBloqueioS(d.bloqueado_s);
          setBloqueioOpen(true);
          return;
        }
        setFacialModo(d.enrolled ? "verify" : "enroll");
        return;
      }
    } catch { /* falha na checagem: backend ainda barra via facial_requerido se o tenant tiver 2FA */ }
    setQrAcessoOpen(true);
  };

  const abrirVoz = async () => {
    if (!vozAtivo) {
      toast.info("Câmeras não configuradas.");
      return;
    }
    setFacialDestino("voz");
    try {
      const r = await fetch(`${API}/public/facial/status?origem=${encodeURIComponent(origem)}&eql=${encodeURIComponent(unidade)}&cpf=${cpf}`);
      const d = await r.json();
      if (d.ok && d.tenant_2fa && !d.liberar_2fa) {
        if ((d.bloqueado_s || 0) > 0) {
          setBloqueioS(d.bloqueado_s);
          setBloqueioOpen(true);
          return;
        }
        setFacialModo(d.enrolled ? "verify" : "enroll");
        return;
      }
    } catch { /* falha na checagem */ }
    toast.info("Câmeras não configuradas.");
  };
  const [convitesOpen, setConvitesOpen] = useState(false);
  const [preAgendaOpen, setPreAgendaOpen] = useState(false);
  const [visitantesAtivos, setVisitantesAtivos] = useState(0);
  const [reservasValidas, setReservasValidas] = useState(0);
  const [convitesAtivos, setConvitesAtivos] = useState(0);
  const [agendasAtivas, setAgendasAtivas] = useState(0);
  const unidadeRef = useRef(null);
  const admRef = useRef(null);
  const avisosRef = useRef(null);

  const toggleCollapsible = (aberto, setAberto, ref) => {
    setAberto(!aberto);
    if (!aberto) setTimeout(() => ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 380);
  };

  const voltarHome = () => {
    setMenuOpen(false);
    setEmergOpen(false);
    setTodosOpen(false);
    setAvisoView(null);
    setSolicOpen(false);
    setPerfilOpen(false);
    setReservasOpen(false);
    setFamiliaresOpen(false);
    setPrestadoresOpen(false);
    setVisitantesOpen(false);
    setQrAcessoOpen(false);
    setFacialModo(null);
    setFacialToken(null);
    setVozOpen(false);
    setConvitesOpen(false);
    setOpenUnidade(false);
    setOpenAdm(false);
    setOpenAvisos(false);
    setBlinkEmerg(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
    carregarAvisos();
  };

  const abrirUnidade = () => {
    setOpenUnidade(true);
    setTimeout(() => unidadeRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 380);
  };

  const acionar = async (tipo) => {
    setEnviando(tipo);
    try {
      const r = await fetch(`${API}/public/emergencia`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ origem, eql: unidade, cpf, tipo }),
      });
      const d = await r.json();
      if (d.ok) {
        setEmergOpen(false);
        setBlinkEmerg(true);
        setTimeout(() => setBlinkEmerg(false), 1100);
        if (d.duplicado) toast.info("Chamado já estava na fila de monitoramento.");
        else toast.success("Emergência registrada na fila de monitoramento.");
      } else {
        toast.error("Não foi possível registrar a emergência. Tente novamente.");
      }
    } catch {
      toast.error("Falha de comunicação. Tente novamente.");
    } finally {
      setEnviando(null);
    }
  };

  const carregarAvisos = async () => {
    try {
      const r = await fetch(`${API}/public/avisos?origem=${encodeURIComponent(origem)}&eql=${encodeURIComponent(unidade)}&cpf=${cpf}`);
      const d = await r.json();
      if (d.ok) { setAvisos(d.avisos); setNaoLidos(d.nao_lidos); }
    } catch { /* polling silencioso */ }
    try {
      const r2 = await fetch(`${API}/public/minhas-manifestacoes?origem=${encodeURIComponent(origem)}&eql=${encodeURIComponent(unidade)}&cpf=${cpf}`);
      const d2 = await r2.json();
      if (d2.ok) setSolicAndamento(d2.itens.filter((m) => m.status !== "resolvido" && m.status !== "cancelado").length);
    } catch { /* polling silencioso */ }
    try {
      const r3 = await fetch(`${API}/public/minhas-reservas-stats?origem=${encodeURIComponent(origem)}&eql=${encodeURIComponent(unidade)}&cpf=${cpf}`);
      const d3 = await r3.json();
      if (d3.ok) setReservasValidas(d3.ativas);
    } catch { /* polling silencioso */ }
    try {
      const r4 = await fetch(`${API}/public/visitantes?origem=${encodeURIComponent(origem)}&eql=${encodeURIComponent(unidade)}&cpf=${cpf}&limit=1`);
      const d4 = await r4.json();
      if (d4.ok) setVisitantesAtivos(d4.total_ativos);
    } catch { /* polling silencioso */ }
    try {
      const r5 = await fetch(`${API}/public/chave-virtual/convites?origem=${encodeURIComponent(origem)}&eql=${encodeURIComponent(unidade)}&cpf=${cpf}`);
      const d5 = await r5.json();
      if (d5.ok) setConvitesAtivos((d5.convites || []).filter((c) => c.status === "ativo").length);
    } catch { /* polling silencioso */ }
    try {
      const r6 = await fetch(`${API}/public/agendamento/visitantes?origem=${encodeURIComponent(origem)}&eql=${encodeURIComponent(unidade)}&cpf=${cpf}`);
      const d6 = await r6.json();
      if (d6.ok) setAgendasAtivas((d6.visitantes || []).filter(agendaVigenteOuFutura).length);
    } catch { /* polling silencioso */ }
  };

  useEffect(() => {
    carregarAvisos();
    carregarVoz();
    const t = setInterval(carregarAvisos, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const abrirAviso = (a) => {
    setAvisoView(a);
    if (!a.lido) {
      fetch(`${API}/public/avisos-lido`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ origem, cpf, aviso_id: a.id }),
      }).catch(() => {});
      setAvisos((prev) => prev.map((x) => (x.id === a.id ? { ...x, lido: true } : x)));
      setNaoLidos((n) => Math.max(0, n - 1));
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "#11131b", fontFamily: FONT_MAIN }} data-testid="appmobile-dashboard">
      <style>{EMERG_CSS}</style>
      {/* 1 - Header */}
      <header data-testid="appmobile-dash-header"
        className="sticky top-0 z-10 flex items-center gap-3 px-4 py-3.5 max-w-md mx-auto w-full"
        style={{ background: "rgba(17, 19, 27, 0.85)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", borderBottom: "1px solid rgba(255,255,255,0.06)", paddingTop: "max(14px, env(safe-area-inset-top))" }}>
        <button type="button" data-testid="appmobile-dash-menu-btn" onClick={() => setMenuOpen(true)}
          aria-label="Abrir menu"
          className="w-10 h-10 flex items-center justify-center rounded-xl active:bg-white/10 transition-colors"
          style={{ color: "#c3c6d7", touchAction: "manipulation" }}>
          <Menu className="h-5 w-5" />
        </button>
        <div className="flex items-center gap-2">
          <Box className="h-5 w-5" style={{ color: "#b4c5ff" }} />
          <span className="text-base font-bold tracking-tight" style={{ color: "#b4c5ff" }} data-testid="appmobile-dash-title">Brave Access</span>
        </div>
      </header>

      <main className="max-w-md mx-auto w-full px-4 pt-6 pb-28 flex flex-col gap-4">
        {/* 2 - Identificacao */}
        <section data-testid="appmobile-dash-ident">
          <h1 className="text-xl font-bold tracking-tight" style={{ color: "#e1e2ed" }} data-testid="appmobile-dash-tenant">
            {tenant || condo}
          </h1>
          <p className="text-sm mt-1" style={{ color: "#c3c6d7" }} data-testid="appmobile-dash-userline">
            {unidade}{nome ? ` - ${nome}` : ""}
          </p>
        </section>

        {/* 3 - Acoes rapidas */}
        <section data-testid="appmobile-dash-quick" className="relative rounded-[14px] flex overflow-hidden"
          style={{ background: BLUE, boxShadow: "0 0 20px rgba(37, 99, 235, 0.35)" }}>
          <GlowLine />
          <button type="button" data-testid="appmobile-dash-qr-btn" onClick={() => exigirTermos(abrirQrAcesso)}
            className="flex-1 flex flex-col items-center justify-center gap-1.5 py-4 active:bg-white/10 active:scale-95 transition-all duration-150">
            <QrCode className="h-6 w-6 text-white" />
            <span className="text-xs font-medium text-white">QR Acesso</span>
          </button>
          <button type="button" data-testid="appmobile-dash-perfil-btn" onClick={() => setPerfilOpen(true)}
            className="flex-1 flex flex-col items-center justify-center gap-1.5 py-4 active:bg-white/10 active:scale-95 transition-all duration-150">
            <User className="h-6 w-6 text-white" />
            <span className="text-xs font-medium text-white">Meu Perfil</span>
          </button>
          <button type="button" data-testid="appmobile-dash-unidade-btn" onClick={abrirUnidade}
            className="flex-1 flex flex-col items-center justify-center gap-1.5 py-4 active:bg-white/10 active:scale-95 transition-all duration-150">
            <Home className="h-6 w-6 text-white" />
            <span className="text-xs font-medium text-white">Minha Unidade</span>
          </button>
        </section>

        {/* 4 - Convites / Pre Cadastro */}
        <section data-testid="appmobile-dash-actions" className="grid grid-cols-2 gap-3">
          <button type="button" data-testid="appmobile-dash-convites-btn" onClick={() => exigirTermos(() => setConvitesOpen(true))}
            className="relative overflow-hidden flex items-center justify-center gap-2 rounded-xl py-3.5 active:scale-[0.98] transition-transform"
            style={{ background: BLUE, boxShadow: "0 0 15px rgba(37, 99, 235, 0.3)" }}>
            <GlowLine />
            <Mail className="h-5 w-5 text-white" />
            <span className="text-sm font-semibold text-white">Convites</span>
            {convitesAtivos > 0 && (
              <span data-testid="appmobile-dash-convites-badge"
                className="min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold inline-flex items-center justify-center"
                style={{ background: "rgba(255,255,255,0.25)", color: "#fff" }}>
                {convitesAtivos}
              </span>
            )}
          </button>
          <button type="button" data-testid="appmobile-dash-precadastro-btn" onClick={() => exigirTermos(() => setPreAgendaOpen(true))}
            className="relative overflow-hidden flex items-center justify-center gap-2 rounded-xl py-3.5 active:scale-[0.98] transition-transform"
            style={{ background: BLUE, boxShadow: "0 0 15px rgba(37, 99, 235, 0.3)" }}>
            <GlowLine />
            <FileSpreadsheet className="h-5 w-5 text-white" />
            <span className="text-sm font-semibold text-white">Pré Agenda</span>
            {agendasAtivas > 0 && (
              <span data-testid="appmobile-dash-preagenda-badge"
                className="min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold inline-flex items-center justify-center"
                style={{ background: "rgba(255,255,255,0.25)", color: "#fff" }}>
                {agendasAtivas}
              </span>
            )}
          </button>
        </section>

        {/* 5 - MINHA UNIDADE */}
        <Collapsible icon={Home} iconColor="#f4a261" title="MINHA UNIDADE" open={openUnidade}
          onToggle={() => toggleCollapsible(openUnidade, setOpenUnidade, unidadeRef)} testid="appmobile-dash-unidade-collapsible" sectionRef={unidadeRef}>
          <div className="grid grid-cols-3 gap-3 pt-1">
            <SquareButton icon={Users} label="Familiares" color="#8ab4ff" testid="appmobile-dash-familiares-btn"
              onClick={() => setFamiliaresOpen(true)} />
            <SquareButton icon={Contact} label="Visitantes" color="#f4a261" badge={visitantesAtivos} testid="appmobile-dash-visitantes-btn"
              onClick={() => setVisitantesOpen(true)} />
            <SquareButton icon={HardHat} label="Prestadores" color="#f28b82" testid="appmobile-dash-prestadores-btn"
              onClick={() => setPrestadoresOpen(true)} />
          </div>
        </Collapsible>

        {/* 6 - ADM - SOLICITACOES E RESERVAS */}
        <Collapsible icon={Shield} iconColor="#8ab4ff" title="ADM - SOLICITAÇÕES E RESERVAS" open={openAdm}
          onToggle={() => toggleCollapsible(openAdm, setOpenAdm, admRef)} testid="appmobile-dash-adm-collapsible" sectionRef={admRef}>
          <div className="grid grid-cols-3 gap-3 pt-1">
            <SquareButton icon={FileText} label="Solicitações" color="#b4c5ff" badge={solicAndamento} testid="appmobile-dash-solicitacoes-btn"
              onClick={() => setSolicOpen(true)} />
            <SquareButton icon={CalendarDays} label="Reservas" color="#7ef0a0" badge={reservasValidas} testid="appmobile-dash-reservas-btn"
              onClick={() => setReservasOpen(true)} />
            <SquareButton icon={Megaphone} label="Avisos" color="#ffb4ab" badge={avisos.length} testid="appmobile-dash-avisos-btn"
              onClick={() => setTodosOpen(true)} />
          </div>
        </Collapsible>

        {/* 7 - Avisos */}
        <Collapsible title="Avisos não lidos" icon={null} badge={naoLidos} open={openAvisos}
          onToggle={() => toggleCollapsible(openAvisos, setOpenAvisos, avisosRef)} testid="appmobile-dash-avisos-collapsible" sectionRef={avisosRef}
          right={
            <button type="button" data-testid="appmobile-dash-avisos-ver-todos"
              onClick={(e) => { e.stopPropagation(); setTodosOpen(true); }}
              className="flex items-center gap-1 text-xs font-medium hover:underline"
              style={{ color: "#b4c5ff" }}>
              Todos <ArrowRight className="h-3.5 w-3.5" />
            </button>
          }>
          {avisos.filter((a) => !a.lido).length === 0 ? (
            <p className="text-center text-xs py-8" style={{ color: "#8d90a0" }} data-testid="appmobile-dash-avisos-vazio">
              Nenhum aviso não lido.
            </p>
          ) : (
            <div className="flex flex-col gap-2 max-h-72 overflow-y-auto overflow-x-hidden pt-1 pr-0.5" data-testid="appmobile-dash-avisos-lista">
              {avisos.filter((a) => !a.lido).map((a) => (
                <AvisoItem key={a.id} a={a} onClick={() => abrirAviso(a)} />
              ))}
            </div>
          )}
        </Collapsible>
      </main>

      {/* 8 - Barra fixa inferior (some quando uma tela overlay está aberta) */}
      {!(menuOpen || emergOpen || avisoView || todosOpen || solicOpen || perfilOpen || reservasOpen
        || familiaresOpen || prestadoresOpen || visitantesOpen || qrAcessoOpen || convitesOpen || preAgendaOpen || facialModo || bloqueioOpen || vozOpen) && (
      <nav data-testid="appmobile-dash-tabbar" className="fixed bottom-0 left-0 right-0 z-[60] px-4 pb-3 pb-safe-tabbar">
        <div className="relative max-w-md mx-auto rounded-[14px] flex overflow-hidden" style={glassCard}>
          <GlowLine />
          <TabBarButton icon={Home} label="Home" testid="appmobile-dash-home-btn" color="#8ab4ff"
            onClick={voltarHome} />
          <TabBarButton icon={vozAtivo ? AudioLines : Video} label={vozAtivo ? "Voice" : "Câmera"}
            testid="appmobile-dash-camera-btn" color="#7ef0a0"
            onClick={() => exigirTermos(abrirVoz)} />
          <TabBarButton icon={Siren} label="Emergências" testid="appmobile-dash-emergencias-btn" color="#f4a261"
            blink={blinkEmerg} onClick={() => setEmergOpen(true)} />
          <TabBarButton icon={LogOut} label="Logoff" testid="appmobile-dash-logoff-btn" color="#ffb4ab" onClick={onLogoff} />
        </div>
      </nav>
      )}

      {/* Familiares do morador */}
      {familiaresOpen && (
        <MobileFamiliares origem={origem} eql={unidade} cpf={cpf} onClose={() => setFamiliaresOpen(false)} />
      )}

      {/* Prestadores e familiares autorizáveis */}
      {prestadoresOpen && (
        <MobilePrestadores origem={origem} eql={unidade} cpf={cpf} onClose={() => setPrestadoresOpen(false)} />
      )}

      {/* Visitantes da unidade */}
      {visitantesOpen && (
        <MobileVisitantes origem={origem} eql={unidade} cpf={cpf} onClose={() => setVisitantesOpen(false)} />
      )}

      {/* Facial 2FA: enrollment inline / verificação antes de liberar a câmera do QR */}
      {facialModo && (
        <MobileFacial key={facialModo} origem={origem} eql={unidade} cpf={cpf} modo={facialModo}
          onClose={() => setFacialModo(null)}
          onBloqueado={() => { setFacialModo(null); setBloqueioS(60); setBloqueioOpen(true); }}
          onSucesso={(token) => {
            if (facialModo === "enroll") { setFacialModo("verify"); return; }
            setFacialModo(null);
            setFacialToken(token);
            if (facialDestino === "voz") setVozOpen(true);
            else setQrAcessoOpen(true);
          }} />
      )}

      {/* Acesso por Voz: comandos falados após verificação facial */}
      {vozOpen && (
        <MobileVoz origem={origem} eql={unidade} cpf={cpf} facialToken={facialToken}
          comandos={vozComandos} onClose={() => setVozOpen(false)} />
      )}

      {/* Modal de bloqueio por muitas tentativas faciais */}
      {bloqueioOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-6"
          style={{ background: "rgba(10,11,16,0.78)" }} data-testid="appmobile-facial-bloqueio-overlay">
          <div className="w-full max-w-sm rounded-[14px] p-5 relative overflow-hidden" style={glassCard}
            data-testid="appmobile-facial-bloqueio-modal">
            <GlowLine />
            <h2 className="text-sm font-bold mb-2" style={{ color: "#ffb4ab" }}
              data-testid="appmobile-facial-bloqueio-titulo">QR Acesso temporariamente bloqueado</h2>
            <p className="text-xs leading-relaxed mb-4" style={{ color: "#c3c6d7" }}
              data-testid="appmobile-facial-bloqueio-msg">
              Houve muitas tentativas de reconhecimento facial sem sucesso. O botão está bloqueado por segurança.
              Tente novamente em aproximadamente {bloqueioS >= 50 ? "1 minuto" : `${Math.max(bloqueioS, 5)} segundos`}.
            </p>
            <button type="button" data-testid="appmobile-facial-bloqueio-ok" onClick={() => setBloqueioOpen(false)}
              className="w-full h-11 rounded-xl text-sm font-semibold text-white active:scale-[0.98] transition-transform"
              style={{ background: "#2563EB", touchAction: "manipulation" }}>
              Entendi
            </button>
          </div>
        </div>
      )}

      {/* QR Acesso: scan de QR estático do ponto */}
      {qrAcessoOpen && (
        <MobileQrAcesso origem={origem} eql={unidade} cpf={cpf} facialToken={facialToken} onClose={() => setQrAcessoOpen(false)} />
      )}

      {/* Convites via Chave Virtual */}
      {convitesOpen && (
        <MobileConvites origem={origem} eql={unidade} cpf={cpf}
          onClose={() => setConvitesOpen(false)}
          onAbrirPerfil={() => { setConvitesOpen(false); setPerfilOpen(true); }} />
      )}

      {/* Pré Agenda de visitantes/prestadores */}
      {preAgendaOpen && (
        <MobilePreAgenda origem={origem} eql={unidade} cpf={cpf} nome={nome} tipo={tipo}
          onClose={() => setPreAgendaOpen(false)} />
      )}

      {/* Reservas do morador */}
      {reservasOpen && (
        <MobileReservas origem={origem} eql={unidade} cpf={cpf} nome={nome} onClose={() => setReservasOpen(false)} />
      )}

      {/* Meu Perfil */}
      {perfilOpen && (
        <MobilePerfil origem={origem} eql={unidade} cpf={cpf} onClose={() => setPerfilOpen(false)} />
      )}

      {/* CRUD de solicitacoes do morador */}
      {solicOpen && (
        <MobileSolicitacoes origem={origem} eql={unidade} cpf={cpf} onClose={() => setSolicOpen(false)} />
      )}

      {/* Modal "Ver todos" os avisos */}
      {todosOpen && (
        <div className="fixed inset-0 z-30 flex flex-col" style={{ background: "#11131b", fontFamily: FONT_MAIN }}
          data-testid="appmobile-avisos-todos-modal">
          <header className="flex items-center gap-3 px-4 py-3.5"
            style={{ borderBottom: "1px solid rgba(255,255,255,0.06)", paddingTop: "max(14px, env(safe-area-inset-top))" }}>
            <button type="button" data-testid="appmobile-avisos-todos-fechar" aria-label="Fechar"
              onClick={() => setTodosOpen(false)}
              className="w-10 h-10 flex items-center justify-center rounded-xl active:bg-white/10"
              style={{ color: "#c3c6d7", touchAction: "manipulation" }}>
              <X className="h-5 w-5" />
            </button>
            <h1 className="text-base font-bold" style={{ color: "#b4c5ff" }} data-testid="appmobile-avisos-todos-titulo">Todos os Avisos</h1>
          </header>
          <main className="flex-1 overflow-y-auto px-4 py-4 pb-28">
            {avisos.length === 0 ? (
              <p className="text-center text-xs py-16" style={{ color: "#8d90a0" }} data-testid="appmobile-avisos-todos-vazio">
                Nenhum aviso ativo no momento.
              </p>
            ) : (
              <div className="flex flex-col gap-2" data-testid="appmobile-avisos-todos-lista">
                {avisos.map((a) => (
                  <AvisoItem key={a.id} a={a} onClick={() => abrirAviso(a)} />
                ))}
              </div>
            )}
          </main>
        </div>
      )}

      {/* Card de leitura do aviso */}
      {avisoView && (
        <div className="fixed inset-0 z-40 flex items-center justify-center px-6" data-testid="appmobile-aviso-modal"
          onClick={() => setAvisoView(null)}>
          <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.7)" }} />
          <div className="relative w-full max-w-[300px] rounded-[14px] overflow-hidden" style={glassCard}
            onClick={(e) => e.stopPropagation()} data-testid="appmobile-aviso-card">
            {avisoView.image_path && (
              <div className="w-full aspect-square flex items-center justify-center" style={{ background: "rgba(0,0,0,0.35)" }}>
                <img src={`${API}/public/aviso-imagem/${avisoView.id}?origem=${encodeURIComponent(origem)}`}
                  alt={avisoView.title} data-testid="appmobile-aviso-imagem"
                  className="max-w-full max-h-full object-contain" />
              </div>
            )}
            <div className="p-4 space-y-2">
              <h3 className="text-sm font-semibold" style={{ color: "#e1e2ed" }} data-testid="appmobile-aviso-titulo">{avisoView.title}</h3>
              <p className="text-xs whitespace-pre-wrap" style={{ color: "#c3c6d7" }} data-testid="appmobile-aviso-texto">{avisoView.text}</p>
              <p className="text-[10px]" style={{ color: "#8d90a0" }}>{fmtAvisoData(avisoView.created_at)}</p>
              <button type="button" data-testid="appmobile-aviso-fechar" onClick={() => setAvisoView(null)}
                className="w-full mt-1 py-2.5 rounded-xl text-xs font-medium active:scale-[0.98] transition-transform"
                style={{ color: "#e1e2ed", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de emergencias */}
      {emergOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center px-6" data-testid="appmobile-emerg-modal">
          <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.7)" }} onClick={() => !enviando && setEmergOpen(false)} />
          <div className="relative w-full max-w-xs rounded-[14px] overflow-hidden p-5" style={glassCard}>
            <GlowLine />
            <h2 className="text-base font-bold text-center" style={{ color: "#e1e2ed" }} data-testid="appmobile-emerg-titulo">Emergências</h2>
            <p className="text-xs text-center mt-1 mb-4" style={{ color: "#8d90a0" }}>Toque para acionar o monitoramento</p>
            <div className="grid grid-cols-3 gap-3">
              {[
                { tipo: "panico", label: "Pânico", Icon: Siren, cor: "#ffb4ab", tid: "appmobile-emerg-panico" },
                { tipo: "medica", label: "Emergência Médica", Icon: Ambulance, cor: "#7ef0a0", tid: "appmobile-emerg-medica" },
                { tipo: "incendio", label: "Incêndio", Icon: Flame, cor: "#f4a261", tid: "appmobile-emerg-incendio" },
              ].map(({ tipo, label, Icon, cor, tid }) => (
                <button key={tipo} type="button" data-testid={tid} disabled={!!enviando}
                  onClick={() => acionar(tipo)}
                  className="flex flex-col items-center gap-2 rounded-xl py-3 active:scale-95 transition-all disabled:opacity-50">
                  {enviando === tipo
                    ? <span className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ background: `${cor}22`, border: `1px solid ${cor}33` }}><Loader2 className="h-6 w-6 animate-spin" style={{ color: cor }} /></span>
                    : <IconBadge icon={Icon} color={cor} size="h-6 w-6" box="w-12 h-12" />}
                  <span className="text-[11px] font-medium text-center leading-tight" style={{ color: "#c3c6d7" }}>{label}</span>
                </button>
              ))}
            </div>
            <button type="button" data-testid="appmobile-emerg-cancelar" onClick={() => !enviando && setEmergOpen(false)}
              className="mt-4 w-full py-2.5 rounded-xl text-xs font-medium active:scale-[0.98] transition-transform"
              style={{ color: "#8d90a0", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}>
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Menu lateral */}
      {menuOpen && (
        <div className="fixed inset-0 z-30" data-testid="appmobile-dash-drawer-overlay" onClick={() => setMenuOpen(false)}>
          <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.6)" }} />
          <aside data-testid="appmobile-dash-drawer"
            className="absolute left-0 top-0 bottom-0 w-64 p-5 flex flex-col gap-1"
            style={{ ...glassCard, borderRadius: 0,
              transform: drawerDrag ? `translateX(${drawerDrag}px)` : undefined,
              transition: drawerDrag ? "none" : "transform 0.18s ease-out" }}
            onClick={(e) => e.stopPropagation()}
            onTouchStart={(e) => { drawerTouchRef.current = e.touches[0].clientX; drawerDragRef.current = 0; }}
            onTouchMove={(e) => {
              if (drawerTouchRef.current == null) return;
              const dx = e.touches[0].clientX - drawerTouchRef.current;
              drawerDragRef.current = Math.min(0, dx);
              setDrawerDrag(drawerDragRef.current);
            }}
            onTouchEnd={() => {
              if (drawerDragRef.current < -60) setMenuOpen(false);
              drawerDragRef.current = 0;
              setDrawerDrag(0);
              drawerTouchRef.current = null;
            }}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Box className="h-5 w-5" style={{ color: "#b4c5ff" }} />
                <span className="text-sm font-bold" style={{ color: "#b4c5ff" }}>Brave Access</span>
              </div>
              <button type="button" data-testid="appmobile-dash-drawer-close" onClick={() => setMenuOpen(false)}
                aria-label="Fechar menu"
                className="w-9 h-9 flex items-center justify-center rounded-xl active:bg-white/10"
                style={{ color: "#c3c6d7", touchAction: "manipulation" }}>
                <X className="h-5 w-5" />
              </button>
            </div>
            <button type="button" data-testid="appmobile-dash-drawer-home"
              onClick={() => { setMenuOpen(false); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-left active:bg-white/10 transition-colors">
              <Home className="h-4 w-4" style={{ color: "#c3c6d7" }} />
              <span className="text-sm" style={{ color: "#e1e2ed" }}>Home</span>
            </button>
            <button type="button" data-testid="appmobile-dash-drawer-camera"
              onClick={() => { setMenuOpen(false); toast.info("Câmeras não configuradas."); }}
              className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-left active:bg-white/10 transition-colors">
              <Video className="h-4 w-4" style={{ color: "#c3c6d7" }} />
              <span className="text-sm" style={{ color: "#e1e2ed" }}>Câmera</span>
            </button>
            <button type="button" data-testid="appmobile-dash-drawer-responsabilidades"
              onClick={() => { setMenuOpen(false); setRespAberto(true); }}
              className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-left active:bg-white/10 transition-colors">
              <Info className="h-4 w-4" style={{ color: "#c3c6d7" }} />
              <span className="text-sm" style={{ color: "#e1e2ed" }}>Responsabilidades</span>
            </button>
            <a href="https://app.braveaccess.com.br/politica-privacidade.html" target="_blank" rel="noopener noreferrer"
              data-testid="appmobile-dash-drawer-politica"
              className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-left active:bg-white/10 transition-colors">
              <FileText className="h-4 w-4" style={{ color: "#c3c6d7" }} />
              <span className="text-sm" style={{ color: "#e1e2ed" }}>Política de Privacidade</span>
            </a>
            <button type="button" data-testid="appmobile-dash-drawer-excluir"
              onClick={() => { setMenuOpen(false); window.location.href = `/excluir-conta?cpf=${cpf}&origem=${encodeURIComponent(origem)}`; }}
              className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-left active:bg-white/10 transition-colors">
              <UserX className="h-4 w-4" style={{ color: "#ffb4ab" }} />
              <span className="text-sm" style={{ color: "#ffb4ab" }}>Excluir minha Conta</span>
            </button>
            <button type="button" data-testid="appmobile-dash-drawer-logoff"
              onClick={() => { setMenuOpen(false); onLogoff(); }}
              className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-left active:bg-white/10 transition-colors">
              <LogOut className="h-4 w-4" style={{ color: "#ffb4ab" }} />
              <span className="text-sm" style={{ color: "#ffb4ab" }}>Logoff</span>
            </button>
            <div className="mt-auto pt-4" style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
              <p className="text-[11px]" style={{ color: "#8d90a0" }}>{REVENDA}</p>
              {condo && <p className="text-[11px] mt-0.5" style={{ color: "#8d90a0" }} data-testid="appmobile-dash-drawer-condo">{condo}</p>}
            </div>
          </aside>
        </div>
      )}
      <MobilePrivacyConsent />
      <MobileResponsabilidades aberto={respAberto} onFechar={() => setRespAberto(false)} />
    </div>
  );
}
